import { ReactNode, useCallback, useEffect, useMemo, useRef, useState, MouseEvent } from 'react';
import { TooltipPickingInfo } from '../contexts/MapTooltipProvider';
import { useTheme } from '@mui/material';
import useDeckGlMapStyles from './styles';

export const fallbackGetTooltip = () => {
  if (process.env.NODE_ENV === 'development') {
    // just for development env
    // eslint-disable-next-line no-console
    console.log('noTooltipFound for object');
  }
  return null;
};

export function MapTooltip({
  info,
  width,
  height,
}: {
  info: TooltipPickingInfo<{
    getTooltip?: (object: any) => ReactNode;
    properties?: { getTooltip?: (object: any) => ReactNode };
  }>;
  width: number;
  height: number;
}) {
  const { object, x, y } = info;
  const { spacing } = useTheme();
  const classes = useDeckGlMapStyles();
  const getTooltip = object?.getTooltip ?? object?.properties?.getTooltip ?? fallbackGetTooltip;

  // Get the tooltip content
  const tooltipContent = useMemo(() => {
    // TODO: check with cluster disabled
    return getTooltip(object);
    // watch the object so we can update the tooltip
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [object, getTooltip]);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    let refTimer = setTimeout(() => {
      if (tooltipRef.current) {
        const { width, height } = tooltipRef.current.getBoundingClientRect();
        if (width === 0 || height === 0 || !tooltipContent) {
          return;
        }
        setTooltipSize({ width, height });
        clearTimeout(refTimer);
      }
    }, 100);
    return () => clearTimeout(refTimer);
  }, [tooltipContent]);

  const maxLeft = width - tooltipSize.width * 1.1;
  const maxTop = height - tooltipSize.height * 1.1;
  let left = (x ?? 0) + 10;
  let top = (y ?? 0) + 10;
  if (left > maxLeft) {
    left = maxLeft;
  }
  if (top > maxTop) {
    top = maxTop;
  }
  const stopEventPropagation = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);
  return (
    <div
      className='tooltip'
      autoFocus
      onMouseMoveCapture={stopEventPropagation}
      onMouseDownCapture={stopEventPropagation}
      onWheelCapture={stopEventPropagation}
      style={{
        visibility: tooltipSize.width > 0 ? 'visible' : 'hidden',
        left,
        top,
        marginTop: `-${spacing(2)}`,
        marginLeft: `-${spacing(2)}`,
        pointerEvents: 'auto',
        position: 'absolute',
        zIndex: 9999,
      }}
      ref={tooltipRef}
    >
      <div className={classes.innerMapTooltip}>{tooltipContent}</div>
    </div>
  );
}
